import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  BookOpen, Sparkles, Wand2, Plus, Image as ImageIcon, Upload,
  Trash2, Edit3, ExternalLink, Eye, Send, Loader2, Code2, Copy,
  RefreshCcw, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { blogApi, uploadApi, aiApi } from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import DataPagination, { usePagination } from '../../components/DataPagination';

const PUBLIC_BLOG_ORIGIN = window.location.origin;

function statusPill(status) {
  if (status === 'published') {
    return (
      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-900">
        Published
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-900">
      Draft
    </Badge>
  );
}

export default function Blogs() {
  // --- Settings (page name + slug + website URL) ---
  const [settings, setSettings] = useState(null);
  const [pageName, setPageName] = useState('');
  const [slug, setSlug] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);

  // --- Ideas + Generator ---
  const [topic, setTopic] = useState('');
  const [ideas, setIdeas] = useState([]);
  const [loadingIdeas, setLoadingIdeas] = useState(false);
  const [generatingIdeaTitle, setGeneratingIdeaTitle] = useState(null); // which idea is currently generating

  // --- Editor dialog ---
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editorTitle, setEditorTitle] = useState('');
  const [editorContent, setEditorContent] = useState('');
  const [editorExcerpt, setEditorExcerpt] = useState('');
  const [editorImageUrl, setEditorImageUrl] = useState('');
  const [editorBusy, setEditorBusy] = useState(false);
  const [imageUploadBusy, setImageUploadBusy] = useState(false);
  const [imagePrompt, setImagePrompt] = useState('');
  const [aiImageBusy, setAiImageBusy] = useState(false);

  // --- Drafts table ---
  const [blogs, setBlogs] = useState([]);
  const [loadingBlogs, setLoadingBlogs] = useState(true);

  const pagination = usePagination(blogs, 10, [blogs.length]);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await blogApi.getSettings();
      setSettings(data.settings);
      if (data.settings) {
        setPageName(data.settings.page_name || '');
        setSlug(data.settings.slug || '');
        setWebsiteUrl(data.settings.website_url || '');
      }
    } catch (e) {
      toast.error('Failed to load blog settings');
    }
  }, []);

  const loadBlogs = useCallback(async () => {
    setLoadingBlogs(true);
    try {
      const { data } = await blogApi.list();
      setBlogs(data.blogs || []);
    } catch (e) {
      toast.error('Failed to load blogs');
    } finally {
      setLoadingBlogs(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
    loadBlogs();
  }, [loadSettings, loadBlogs]);

  const publicUrl = useMemo(() => {
    if (!settings?.slug) return '';
    return `${PUBLIC_BLOG_ORIGIN}/blog/${settings.slug}`;
  }, [settings]);

  const handleSaveSettings = async () => {
    if (!pageName.trim()) return toast.error('Please enter a blog page name');
    const trimmedUrl = websiteUrl.trim();
    if (trimmedUrl && !/^https?:\/\//i.test(trimmedUrl)) {
      return toast.error('Website URL must start with http:// or https://');
    }
    setSavingSettings(true);
    try {
      const payload = {
        page_name: pageName.trim(),
        website_url: trimmedUrl || null,
      };
      if (slug.trim()) payload.slug = slug.trim().toLowerCase();
      const { data } = await blogApi.updateSettings(payload);
      setSettings(data.settings);
      setSlug(data.settings?.slug || '');
      setWebsiteUrl(data.settings?.website_url || '');
      toast.success('Blog page saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSuggestIdeas = async () => {
    if (!topic.trim() || topic.trim().length < 3) {
      return toast.error('Enter a topic or keywords (min 3 chars)');
    }
    setLoadingIdeas(true);
    try {
      const { data } = await blogApi.suggestIdeas({ topic: topic.trim(), count: 5 });
      setIdeas(data.ideas || []);
      if ((data.ideas || []).length === 0) toast.info('No ideas generated. Try a different topic.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate ideas');
    } finally {
      setLoadingIdeas(false);
    }
  };

  const openEditorForIdea = async (idea) => {
    setGeneratingIdeaTitle(idea.title);
    try {
      const { data } = await blogApi.generate({ title: idea.title, hook: idea.hook });
      setEditingId(null);
      setEditorTitle(data.title);
      setEditorContent(data.content);
      setEditorExcerpt(data.excerpt);
      setEditorImageUrl('');
      setImagePrompt(idea.title);
      setEditorOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate blog');
    } finally {
      setGeneratingIdeaTitle(null);
    }
  };

  const openEditorForBlog = (blog) => {
    setEditingId(blog.id);
    setEditorTitle(blog.title);
    setEditorContent(blog.content);
    setEditorExcerpt(blog.excerpt || '');
    setEditorImageUrl(blog.image_url || '');
    setImagePrompt(blog.title);
    setEditorOpen(true);
  };

  const openEditorBlank = () => {
    setEditingId(null);
    setEditorTitle('');
    setEditorContent('');
    setEditorExcerpt('');
    setEditorImageUrl('');
    setImagePrompt('');
    setEditorOpen(true);
  };

  const handleSaveBlog = async () => {
    if (!editorTitle.trim() || editorTitle.trim().length < 3) {
      return toast.error('Title must be at least 3 characters');
    }
    if (!editorContent.trim() || editorContent.trim().length < 10) {
      return toast.error('Content is too short');
    }
    setEditorBusy(true);
    try {
      const payload = {
        title: editorTitle.trim(),
        content: editorContent.trim(),
        excerpt: editorExcerpt.trim(),
        image_url: editorImageUrl || null,
      };
      if (editingId) {
        await blogApi.update(editingId, payload);
        toast.success('Blog updated');
      } else {
        await blogApi.create(payload);
        toast.success('Blog saved as draft');
      }
      setEditorOpen(false);
      loadBlogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setEditorBusy(false);
    }
  };

  const handleImageFile = async (file) => {
    if (!file) return;
    setImageUploadBusy(true);
    try {
      const { data } = await uploadApi.uploadImages([file]);
      const url = data?.urls?.[0];
      if (url) setEditorImageUrl(url);
      else toast.error('Upload failed');
    } catch (e) {
      toast.error('Upload failed');
    } finally {
      setImageUploadBusy(false);
    }
  };

  const handleAiImage = async () => {
    if (!imagePrompt.trim()) return toast.error('Enter an image prompt');
    setAiImageBusy(true);
    try {
      const { data } = await aiApi.generateImage({ prompt: imagePrompt.trim(), number_of_images: 1 });
      const url = data?.urls?.[0];
      if (url) {
        setEditorImageUrl(url);
        toast.success('Image generated');
      } else {
        toast.error('No image returned');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Image generation failed');
    } finally {
      setAiImageBusy(false);
    }
  };

  const handlePublish = async (blog) => {
    try {
      await blogApi.publish(blog.id);
      toast.success('Blog published');
      loadBlogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to publish');
    }
  };

  const handleUnpublish = async (blog) => {
    try {
      await blogApi.unpublish(blog.id);
      toast.success('Moved to draft');
      loadBlogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to unpublish');
    }
  };

  const handleDelete = async (blog) => {
    if (!window.confirm(`Delete "${blog.title}"? This cannot be undone.`)) return;
    try {
      await blogApi.remove(blog.id);
      toast.success('Blog deleted');
      loadBlogs();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const copyText = async (text, label = 'Copied') => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch {
      toast.error('Unable to copy');
    }
  };

  const iframeCode = useMemo(() => {
    if (!publicUrl) return '';
    return `<iframe src="${publicUrl}" width="100%" height="800" frameborder="0" style="border:0;" loading="lazy" title="${pageName || 'Blog'}"></iframe>`;
  }, [publicUrl, pageName]);

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="blogs-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <BookOpen size={22} className="text-primary" /> Blogs
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Generate AI blogs with images, save as drafts, and publish to your public blog page.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openEditorBlank} data-testid="new-blog-btn">
            <Plus size={14} className="mr-2" /> Write manually
          </Button>
        </div>
      </div>

      {/* Settings card */}
      <Card data-testid="blog-settings-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles size={16} className="text-primary" /> Blog page setup
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="page-name" className="text-xs">Blog page name *</Label>
              <Input
                id="page-name"
                placeholder="e.g. Grand Hotel Stories"
                value={pageName}
                onChange={(e) => setPageName(e.target.value)}
                data-testid="page-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="slug" className="text-xs">URL slug (a-z, 0-9, hyphens)</Label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground whitespace-nowrap">/blog/</span>
                <Input
                  id="slug"
                  placeholder="grand-hotel-stories"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase())}
                  data-testid="slug-input"
                />
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="website-url" className="text-xs flex items-center gap-1.5">
              <ExternalLink size={11} /> Website URL <span className="text-muted-foreground font-normal">(optional — shown as &ldquo;Contact / Visit Us&rdquo; CTA at the end of every public blog)</span>
            </Label>
            <Input
              id="website-url"
              type="url"
              placeholder="https://www.your-business.com"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              data-testid="website-url-input"
            />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-2">
            <Button onClick={handleSaveSettings} disabled={savingSettings} data-testid="save-settings-btn">
              {savingSettings ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
              Save blog page
            </Button>

            {publicUrl && (
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-muted-foreground">Public link:</span>
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline truncate max-w-[260px]"
                  data-testid="public-url-link"
                >
                  {publicUrl}
                </a>
                <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => copyText(publicUrl, 'Public link copied')} data-testid="copy-public-url">
                  <Copy size={12} className="mr-1" /> Copy
                </Button>
                <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => copyText(iframeCode, 'Iframe code copied')} data-testid="copy-iframe-btn">
                  <Code2 size={12} className="mr-1" /> Copy iframe
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* AI ideas generator */}
      <Card data-testid="ideas-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Wand2 size={16} className="text-primary" /> Generate blog ideas
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Label htmlFor="topic" className="text-xs">Topic or keywords</Label>
          <Textarea
            id="topic"
            placeholder="e.g. luxury weekend getaways, family-friendly travel tips, sustainable hospitality"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            rows={2}
            data-testid="topic-input"
          />
          <div className="flex gap-2">
            <Button onClick={handleSuggestIdeas} disabled={loadingIdeas} data-testid="suggest-ideas-btn">
              {loadingIdeas ? <Loader2 size={14} className="animate-spin mr-2" /> : <Sparkles size={14} className="mr-2" />}
              Suggest ideas
            </Button>
            {ideas.length > 0 && (
              <Button variant="ghost" onClick={() => setIdeas([])} className="text-muted-foreground">
                Clear
              </Button>
            )}
          </div>

          {ideas.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-2" data-testid="ideas-list">
              {ideas.map((idea) => (
                <button
                  key={idea.title}
                  className="text-left p-3 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-colors group disabled:opacity-60 disabled:cursor-wait"
                  onClick={() => openEditorForIdea(idea)}
                  disabled={!!generatingIdeaTitle}
                  data-testid={`idea-${idea.title.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium leading-snug">{idea.title}</p>
                      {idea.hook && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{idea.hook}</p>}
                    </div>
                    {generatingIdeaTitle === idea.title ? (
                      <Loader2 size={14} className="animate-spin text-primary shrink-0 mt-1" />
                    ) : (
                      <ChevronRight size={14} className="text-muted-foreground group-hover:text-primary shrink-0 mt-1" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Drafts table */}
      <Card data-testid="blogs-table-card">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base">All blogs ({blogs.length})</CardTitle>
          <Button size="sm" variant="ghost" onClick={loadBlogs} className="h-8">
            <RefreshCcw size={12} className="mr-1" /> Refresh
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Title</th>
                  <th className="text-left px-4 py-2.5 font-medium hidden md:table-cell">Excerpt</th>
                  <th className="text-left px-4 py-2.5 font-medium">Status</th>
                  <th className="text-left px-4 py-2.5 font-medium hidden lg:table-cell">Created</th>
                  <th className="text-right px-4 py-2.5 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="blogs-tbody">
                {loadingBlogs ? (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground"><Loader2 size={18} className="animate-spin inline" /></td></tr>
                ) : pagination.pageItems.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">No blogs yet. Generate one using the form above.</td></tr>
                ) : pagination.pageItems.map((b) => (
                  <tr key={b.id} className="border-t border-border hover:bg-muted/30" data-testid={`blog-row-${b.id}`}>
                    <td className="px-4 py-3 align-top">
                      <div className="flex items-start gap-3">
                        {b.image_url ? (
                          <img src={b.image_url} alt="" className="w-10 h-10 rounded object-cover shrink-0" />
                        ) : (
                          <div className="w-10 h-10 rounded bg-muted flex items-center justify-center shrink-0">
                            <BookOpen size={14} className="text-muted-foreground" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="font-medium leading-tight line-clamp-2">{b.title}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-muted-foreground max-w-md align-top">
                      <p className="line-clamp-2 text-xs">{b.excerpt}</p>
                    </td>
                    <td className="px-4 py-3 align-top">{statusPill(b.status)}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-xs text-muted-foreground align-top">
                      {b.created_at ? new Date(b.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-right align-top">
                      <div className="flex justify-end gap-1 flex-wrap">
                        {b.status === 'published' && settings?.slug && (
                          <a
                            href={`${PUBLIC_BLOG_ORIGIN}/blog/${settings.slug}/${b.blog_slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-border hover:bg-muted"
                            data-testid={`preview-${b.id}`}
                          >
                            <Eye size={12} /> View
                          </a>
                        )}
                        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => openEditorForBlog(b)} data-testid={`edit-${b.id}`}>
                          <Edit3 size={12} />
                        </Button>
                        {b.status === 'draft' ? (
                          <Button size="sm" className="h-7 px-2" onClick={() => handlePublish(b)} data-testid={`publish-${b.id}`}>
                            <Send size={12} className="mr-1" /> Publish
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => handleUnpublish(b)} data-testid={`unpublish-${b.id}`}>
                            Unpublish
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(b)} data-testid={`delete-${b.id}`}>
                          <Trash2 size={12} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DataPagination {...pagination} itemLabel="blogs" testIdPrefix="blogs-pagination" />
        </CardContent>
      </Card>

      {/* Editor dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="w-[calc(100vw-1rem)] sm:max-w-3xl max-h-[calc(100vh-1rem)] sm:max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-5 py-4 border-b border-border">
            <DialogTitle className="text-base">
              {editingId ? 'Edit blog' : 'New blog'}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="editor-body">
            <div className="space-y-1.5">
              <Label htmlFor="blog-title" className="text-xs">Title *</Label>
              <Input
                id="blog-title"
                value={editorTitle}
                onChange={(e) => setEditorTitle(e.target.value)}
                data-testid="editor-title-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="blog-content" className="text-xs">Content (Markdown supported) *</Label>
              <Textarea
                id="blog-content"
                value={editorContent}
                onChange={(e) => setEditorContent(e.target.value)}
                rows={14}
                className="font-mono text-xs leading-relaxed"
                data-testid="editor-content-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="blog-excerpt" className="text-xs">Excerpt (shown on public list page)</Label>
              <Textarea
                id="blog-excerpt"
                value={editorExcerpt}
                onChange={(e) => setEditorExcerpt(e.target.value)}
                rows={2}
                maxLength={300}
                data-testid="editor-excerpt-input"
              />
            </div>

            {/* Image section */}
            <div className="space-y-2 border border-border rounded-lg p-3 bg-muted/30">
              <div className="flex items-center justify-between">
                <Label className="text-xs flex items-center gap-1.5">
                  <ImageIcon size={12} /> Featured image
                </Label>
                {editorImageUrl && (
                  <button
                    type="button"
                    onClick={() => setEditorImageUrl('')}
                    className="text-xs text-red-600 hover:underline"
                    data-testid="editor-clear-image"
                  >
                    Remove
                  </button>
                )}
              </div>
              {editorImageUrl ? (
                <img src={editorImageUrl} alt="" className="w-full max-h-56 object-cover rounded" />
              ) : (
                <div className="text-xs text-muted-foreground italic">No image attached</div>
              )}
              <div className="flex flex-col sm:flex-row gap-2 pt-1">
                <label className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleImageFile(e.target.files?.[0])}
                    data-testid="editor-upload-input"
                  />
                  <span className="inline-flex w-full justify-center items-center gap-2 text-xs px-3 py-2 rounded border border-border hover:bg-muted cursor-pointer">
                    {imageUploadBusy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                    Upload image
                  </span>
                </label>
                <div className="flex-[2] flex gap-2">
                  <Input
                    placeholder="AI image prompt"
                    value={imagePrompt}
                    onChange={(e) => setImagePrompt(e.target.value)}
                    className="text-xs"
                    data-testid="editor-image-prompt"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleAiImage}
                    disabled={aiImageBusy}
                    data-testid="editor-ai-image-btn"
                  >
                    {aiImageBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="px-5 py-3 border-t border-border flex-row gap-2 sm:gap-2">
            <Button variant="ghost" onClick={() => setEditorOpen(false)} data-testid="editor-cancel">
              Cancel
            </Button>
            <Button onClick={handleSaveBlog} disabled={editorBusy} data-testid="editor-save">
              {editorBusy ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
              Save draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
